import type { ReactNode, Ref } from "react";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import type {
    FieldPath,
    FieldPathEmptyValue,
    FieldPathValue,
} from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type { KitFieldProps } from "./kitFieldProps.types";

type SelectFieldValue = string | number | null | undefined;

/** Paths of `Values` that hold a string or number choice. */
export type SelectFieldPath<Values> = FieldPath<Values, SelectFieldValue>;

/** The option values a path accepts: its string and number members. */
export type SelectFieldOptionValue<Values, Name extends string> = Extract<
    FieldPathValue<Values, Name>,
    string | number
>;

export interface SelectFieldOption<Option> {
    value: Option;
    label: ReactNode;
    /** Secondary line under the label in the list; the trigger shows only the label. */
    description?: ReactNode;
}

interface SelectControlOptions {
    readOnly?: boolean;
    placeholder?: ReactNode;
    buttonProps?: TooltipTriggerButtonProps;
    portalContainer?: HTMLElement;
}

export type SelectFieldProps<Values, Name extends string> = KitFieldProps<
    Name,
    HTMLButtonElement,
    SelectFieldOptionValue<Values, Name> | FieldPathEmptyValue<Values, Name>
> &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> &
    SelectControlOptions & {
        /** Every option value must fit the path, so a pick stays in type. */
        options: ReadonlyArray<
            SelectFieldOption<SelectFieldOptionValue<Values, Name>>
        >;
    };

interface SelectFieldControlProps<
    Option extends string | number,
> extends SelectControlOptions {
    name: string;
    value: SelectFieldValue;
    options: ReadonlyArray<SelectFieldOption<Option>>;
    isClearable: boolean;
    /** `null` when the trigger's Clear control empties the choice. */
    onValueChange: (value: Option | null) => void;
    onBlur: () => void;
    ref?: Ref<HTMLButtonElement>;
    disabled?: boolean;
    required?: boolean;
    invalid?: boolean;
}

const SelectFieldControl = <Option extends string | number>({
    name,
    value,
    options,
    isClearable,
    onValueChange,
    onBlur,
    ref,
    disabled,
    readOnly,
    required,
    invalid,
    placeholder,
    buttonProps,
    portalContainer,
}: SelectFieldControlProps<Option>) => {
    const selectedOption = options.find((option) => option.value === value);
    const { tooltip, ...triggerButtonProps } = buttonProps ?? {};

    return (
        <Picker.Select<Option>
            name={name}
            value={selectedOption?.value ?? null}
            onValueChange={onValueChange}
            onBlur={onBlur}
            disabled={disabled}
            readOnly={readOnly}
            aria-invalid={invalid}
            container={portalContainer}
        >
            <Picker.ButtonTrigger
                ref={ref}
                render={
                    <Picker.Button
                        variant="primary"
                        {...triggerButtonProps}
                        size={triggerButtonProps.size ?? "md"}
                        aria-required={required ? true : undefined}
                    />
                }
                value={selectedOption?.label}
                placeholder={placeholder}
                tooltip={tooltip}
                clearable={isClearable}
            />
            <Picker.Popup>
                <Picker.List>
                    {options.map((option) => (
                        <Picker.Item
                            key={option.value}
                            value={option.value}
                            description={option.description}
                        >
                            {option.label}
                        </Picker.Item>
                    ))}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};

/**
 * Short static choice field (up to 5 options) bound to a typed Formik path.
 * The trigger shows the selected option's label. Offers the trigger's Clear
 * control only when `clearable` comes with an `emptyValue` the path allows.
 * Longer or searchable lists use a searchable `Picker.Select` inside
 * `FormikField`.
 */
export const SelectField = <
    Values,
    Name extends SelectFieldPath<Values> = SelectFieldPath<Values>,
>(
    props: SelectFieldProps<Values, Name>,
): ReactNode => {
    const {
        name,
        options,
        ref,
        disabled,
        readOnly,
        required,
        onValueChange,
        placeholder,
        buttonProps,
        portalContainer,
    } = props;
    const clearTarget = toClearTarget(props);

    return (
        <FormikField<SelectFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => (
                <SelectFieldControl
                    name={name}
                    value={field.value}
                    invalid={invalid}
                    options={options}
                    isClearable={clearTarget !== undefined}
                    onValueChange={(value) => {
                        if (value !== null) {
                            void helpers.setValue(value);
                            onValueChange?.(value);
                        } else if (clearTarget) {
                            void helpers.setValue(clearTarget.emptyValue);
                            onValueChange?.(clearTarget.emptyValue);
                        }
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    ref={ref}
                    disabled={disabled}
                    readOnly={readOnly}
                    required={required}
                    placeholder={placeholder}
                    buttonProps={{
                        ...buttonProps,
                        ...ariaLabelledbyProps(props["aria-labelledby"]),
                    }}
                    portalContainer={portalContainer}
                />
            )}
        </FormikField>
    );
};
